if (!customElements.get('us-wishlist')) {
  customElements.define('us-wishlist', class extends HTMLElement {
    connectedCallback() {
      this.key = 'uniform-standard-wishlist-v1';
      this.abort = new AbortController();
      this.items = this.read();
      this.list = this.querySelector('[data-wishlist-list]');
      this.status = this.querySelector('[data-wishlist-status]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.setupSizePicker();
      document.addEventListener('click', event => {
        const button = event.target.closest('[data-us-wishlist-toggle], [data-wishlist-remove]');
        if (!button) return;
        event.preventDefault();
        const handle = button.dataset.handle;
        if (!/^[a-z0-9-]+$/.test(handle || '')) return;
        const fromDrawer = button.hasAttribute('data-wishlist-remove');
        if (fromDrawer && !this.list.contains(button)) return;
        const index = this.items.findIndex(item => item.handle === handle && (!fromDrawer || String(item.variantId || '') === (button.dataset.variantId || '')));
        if (fromDrawer && index === -1) return;
        const removing = index !== -1;
        if (removing) {
          if (fromDrawer) this.items.splice(index, 1);
          else this.items = this.items.filter(item => item.handle !== handle);
        }
        else { this.chooseSize(button); return; }
        let message = removing ? this.dataset.removed : this.dataset.added;
        try { localStorage.setItem(this.key, JSON.stringify(this.items)); }
        catch { message = this.dataset.storageError; }
        this.render();
        this.status.textContent = message;
        if (fromDrawer) {
          const buttons = this.list.querySelectorAll('[data-wishlist-remove]');
          const next = buttons[Math.min(index, buttons.length - 1)];
          if (next) next.focus();
          else this.empty.focus();
        }
      }, { signal: this.abort.signal });
      window.addEventListener('storage', event => {
        if (event.key === this.key || event.key === null) { this.items = this.read(); this.render(); }
      }, { signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.syncButtons());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.render();
    }
    setupSizePicker() {
      this.picker = this.querySelector('[data-wishlist-picker]');
      this.pickerForm = this.picker.querySelector('form');
      this.pickerImage = document.createElement('img');
      this.pickerImage.className = 'us-wishlist-picker-image';
      this.pickerImage.hidden = true;
      this.pickerImage.alt = '';
      this.pickerImage.width = 600;
      this.pickerImage.height = 600;
      this.pickerImage.addEventListener('error', () => { this.pickerImage.hidden = true; });
      this.picker.querySelector('[data-picker-product]').before(this.pickerImage);
      this.pickerSelect = this.picker.querySelector('select');
      this.setupSizeGrid();
      this.unitControls = this.picker.querySelector('[data-wishlist-units]');
      this.unitControls.addEventListener('click', event => {
        const button = event.target.closest('[data-size-unit]');
        if (!button) return;
        this.sizeUnit = button.dataset.sizeUnit;
        this.updateSizeLabels();
      });
      this.pickerMessage = this.picker.querySelector('[data-picker-message]');
      this.pickerSave = this.picker.querySelector('[type="submit"]');
      this.picker.querySelector('[data-picker-close]').addEventListener('click', () => window.usCloseDialog(this.picker));
      this.picker.addEventListener('close', () => {
        this.productRequest?.abort();
        this.pendingProduct = null;
        this.pickerTrigger?.focus({ preventScroll: true });
      });
      this.pickerForm.addEventListener('submit', event => {
        event.preventDefault();
        const variants = this.pendingProduct?.variants.filter(value => this.selectedSizes.has(String(value.id))) || [];
        if (!variants.length) return;
        variants.forEach(variant => this.saveProduct(this.pendingProduct, variant));
        window.usCloseDialog(this.picker);
      });
    }
    setupSizeGrid() {
      this.pickerSelect.required = false;
      this.pickerSelect.hidden = true;
      this.selectedSizes = new Set();
      this.sizeGrid = document.createElement('div');
      this.sizeGrid.className = 'us-wishlist-size-grid';
      this.sizeGrid.hidden = true;
      this.sizeGrid.setAttribute('role', 'group');
      this.sizeGrid.setAttribute('aria-label', this.dataset.chooseSize);
      this.pickerSelect.after(this.sizeGrid);
    }
    renderSizeGrid() {
      this.sizeGrid.replaceChildren();
      (this.pendingProduct?.variants || []).forEach(variant => {
        const id = String(variant.id);
        const parts = this.sizeParts(variant.title);
        const label = parts.units[this.sizeUnit] ? [parts.units[this.sizeUnit], ...parts.other].join(' / ') : variant.title;
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.setAttribute('aria-label', this.sizeLabel(variant.title, this.sizeUnit) + (variant.available ? '' : ' — ' + this.dataset.soldOut));
        button.setAttribute('aria-pressed', String(this.selectedSizes.has(id)));
        if (!variant.available) {
          const note = document.createElement('small');
          note.textContent = this.dataset.soldOut;
          button.append(note);
        }
        button.addEventListener('click', () => {
          if (this.selectedSizes.has(id)) this.selectedSizes.delete(id);
          else this.selectedSizes.add(id);
          button.setAttribute('aria-pressed', String(this.selectedSizes.has(id)));
          this.pickerSave.disabled = this.selectedSizes.size === 0;
        });
        this.sizeGrid.append(button);
      });
      this.pickerSave.disabled = this.selectedSizes.size === 0;
    }
    async chooseSize(button) {
      if (this.picker.open) return;
      this.pickerTrigger = button;
      this.pickerImage.hidden = true;
      this.pickerImage.removeAttribute('src');
      this.pendingProduct = null;
      const activeUnit = document.querySelector('[data-us-size] .us-size__tab[aria-selected="true"]')?.dataset.unit;
      this.sizeUnit = ['UK', 'EU', 'US'].includes(activeUnit) ? activeUnit : this.dataset.defaultUnit || 'UK';
      this.unitControls.hidden = true;
      this.selectedSizes.clear();
      this.sizeGrid.hidden = true;
      this.pickerSelect.replaceChildren();
      this.pickerSelect.hidden = true;
      this.pickerSave.disabled = true;
      this.pickerMessage.textContent = this.dataset.loading;
      this.picker.querySelector('[data-picker-product]').textContent = button.dataset.title || '';
      this.picker.showModal();
      this.productRequest = new AbortController();
      const root = (this.dataset.root || '/').replace(/\/$/, '') + '/';
      try {
        const response = await fetch(root + 'products/' + button.dataset.handle + '.js', { signal: this.productRequest.signal });
        if (!response.ok) throw new Error('Product unavailable');
        const product = await response.json();
        if (!Array.isArray(product.variants) || !product.variants.length) throw new Error('Missing variants');
        if (!this.picker.open) return;
        this.pendingProduct = product;
        const imageUrl = product.featured_image ? this.safeImage(product.featured_image) : '';
        if (imageUrl) {
          this.pickerImage.src = imageUrl;
          this.pickerImage.alt = product.title;
          this.pickerImage.hidden = false;
        }
        this.picker.querySelector('[data-picker-product]').textContent = product.title;
        const defaultOnly = product.variants.length === 1 && product.variants[0].title === 'Default Title';
        if (defaultOnly) {
          this.saveProduct(product, product.variants[0]);
          window.usCloseDialog(this.picker);
          return;
        }
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.dataset.chooseSize;
        placeholder.disabled = true;
        placeholder.selected = true;
        this.pickerSelect.append(placeholder);
        product.variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title + (variant.available ? '' : ' — ' + this.dataset.soldOut);
          this.pickerSelect.append(option);
        });
        this.updateSizeLabels();
        this.sizeGrid.hidden = false;
        this.pickerMessage.textContent = '';
      } catch (error) {
        if (error.name !== 'AbortError') this.pickerMessage.textContent = this.dataset.loadError;
      }
    }
    sizeParts(title) {
      const units = {};
      const other = [];
      String(title || '').split('/').forEach(part => {
        const match = part.trim().match(/^(UK|EU|US)\s*(.+)$/i);
        if (match) units[match[1].toUpperCase()] = match[2].trim();
        else if (part.trim()) other.push(part.trim());
      });
      return { units, other };
    }
    sizeLabel(title, unit) {
      const parts = this.sizeParts(title);
      return parts.units[unit] ? [unit + ' ' + parts.units[unit], ...parts.other].join(' / ') : title;
    }
    updateSizeLabels() {
      const variants = this.pendingProduct?.variants || [];
      const availableUnits = ['UK', 'EU', 'US'].filter(unit => variants.length && variants.every(variant => this.sizeParts(variant.title).units[unit]));
      this.unitControls.hidden = availableUnits.length < 2;
      if (availableUnits.length && !availableUnits.includes(this.sizeUnit)) this.sizeUnit = availableUnits[0];
      this.unitControls.querySelectorAll('[data-size-unit]').forEach(button => {
        button.hidden = !availableUnits.includes(button.dataset.sizeUnit);
        button.setAttribute('aria-pressed', String(button.dataset.sizeUnit === this.sizeUnit));
      });
      variants.forEach(variant => {
        const option = Array.from(this.pickerSelect.options).find(value => value.value === String(variant.id));
        if (option) option.textContent = this.sizeLabel(variant.title, this.sizeUnit) + (variant.available ? '' : ' — ' + this.dataset.soldOut);
      });
      this.renderSizeGrid();
    }
    saveProduct(product, variant) {
      const item = {
        handle: product.handle,
        title: product.title,
        image: this.safeImage(variant.featured_image?.src || product.featured_image),
        variantId: String(variant.id),
        variantTitle: variant.title,
        sizeUnit: this.sizeUnit
      };
      const index = this.items.findIndex(value => value.handle === item.handle && value.variantId === item.variantId);
      if (index < 0) this.items.push(item);
      else this.items[index] = item;
      let message = this.dataset.added;
      try { localStorage.setItem(this.key, JSON.stringify(this.items)); }
      catch { message = this.dataset.storageError; }
      this.render();
      this.status.textContent = message;
    }
    safeImage(value) {
      try {
        const url = new URL(value, location.origin);
        return url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com') ? url.href : '';
      } catch { return ''; }
    }
    read() {
      try {
        const items = JSON.parse(localStorage.getItem(this.key) || '[]');
        if (!Array.isArray(items)) return [];
        const seen = new Set();
        return items.filter(item => {
          if (!item || !/^[a-z0-9-]+$/.test(item.handle || '') || seen.has(item.handle + ':' + String(item.variantId || ''))) return false;
          seen.add(item.handle + ':' + String(item.variantId || ''));
          return true;
        }).map(item => ({ handle: item.handle, title: String(item.title || item.handle), image: this.safeImage(item.image), variantId: /^\d+$/.test(String(item.variantId || '')) ? String(item.variantId) : '', variantTitle: String(item.variantTitle || ''), sizeUnit: ['UK','EU','US'].includes(item.sizeUnit) ? item.sizeUnit : this.dataset.defaultUnit || 'UK' }));
      } catch { return []; }
    }
    syncButtons() {
      const saved = new Set(this.items.map(item => item.handle));
      document.querySelectorAll('[data-us-wishlist-toggle]').forEach(button => {
        const active = saved.has(button.dataset.handle);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', active ? this.dataset.remove : this.dataset.add);
      });
    }
    render() {
      document.documentElement.classList.toggle('us-wishlist-has-items', this.items.length > 0);
      this.list.replaceChildren();
      const empty = this.empty;
      empty.hidden = this.items.length > 0;
      empty.tabIndex = -1;
      const root = (this.dataset.root || '/').replace(/\/$/, '') + '/';
      this.items.forEach(item => {
        const row = document.createElement('li');
        const imageLink = document.createElement('a');
        imageLink.href = root + 'products/' + item.handle + (item.variantId ? '?variant=' + item.variantId : '');
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 88;
          image.height = 88;
          imageLink.append(image);
        }
        const title = document.createElement('a');
        title.href = imageLink.href;
        title.textContent = item.title;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'us-wishlist-remove';
        remove.dataset.wishlistRemove = '';
        remove.dataset.handle = item.handle;
        remove.dataset.variantId = item.variantId || '';
        remove.setAttribute('aria-label', this.dataset.remove + ': ' + item.title + (item.variantTitle ? ' — ' + this.sizeLabel(item.variantTitle, item.sizeUnit) : ''));
        remove.textContent = '×';
        const details = document.createElement('div');
        details.append(title);
        if (item.variantTitle && item.variantTitle !== 'Default Title') {
          const size = document.createElement('p');
          size.className = 'us-wishlist-size-label';
          size.textContent = this.sizeLabel(item.variantTitle, item.sizeUnit || this.dataset.defaultUnit || 'UK');
          details.append(size);
        }
        row.append(imageLink, details, remove);
        this.list.append(row);
      });
      this.syncButtons();
    }
    disconnectedCallback() {
      this.productRequest?.abort();
      if (this.picker?.open) window.usCloseDialog(this.picker);
      this.abort?.abort();
      this.observer?.disconnect();
    }
  });
}

customElements.whenDefined('x-drawer').then(() => {
  const drawer = document.getElementById('us-wishlist-drawer');
  if (!drawer) return;
  const animateWishlist = entering => {
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
    const content = drawer.getShadowPartByName('content');
    const overlay = drawer.getShadowPartByName('overlay');
    const animations = [
      content.animate({transform: entering ? ['translateY(100%)','translateY(0)'] : ['translateY(0)','translateY(100%)']}, {duration, easing:'ease-out',fill:'both'}),
      overlay.animate({opacity: entering ? [0,1] : [1,0]}, {duration,fill:'both'})
    ];
    return {
      finished: Promise.all(animations.map(animation => animation.finished)).then(() => {
        animations.forEach(animation => animation.cancel());
      }),
      finish() { animations.forEach(animation => animation.finish()); }
    };
  };
  drawer.createEnterAnimationControls = () => animateWishlist(true);
  drawer.createLeaveAnimationControls = () => animateWishlist(false);
});
